import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

export interface FocusDirectiveSettings {
  position?: 'top' | 'bottom' | 'center' | 'nearest';
  offset?: number;
  offsetTop?: number;
  offsetBottom?: number;
  offsetCenter?: number;
  behavior?: 'instant' | 'smooth';
  signature: any;
}

export interface FocusDirectiveConfigs extends FocusDirectiveSettings {
  trigger: EventEmitter<FocusDirectiveSettings>;
}

@Directive({
  selector: '[focus]'
})
export class FocusDirective implements OnInit, OnDestroy {

  @Input()
  public focus: FocusDirectiveConfigs;

  private subscription: Subscription;
  private applyingSettings: FocusDirectiveSettings; 

  constructor(private ref: ElementRef) {    
  }

  ngOnInit() {

    if (this.focus && this.focus.signature && this.focus.trigger) {

      this.subscription = this.focus.trigger.subscribe(settings => {

        let height = NaN;

        until(() => {

          let last = height;
          let next = height = (this.ref.nativeElement as HTMLElement).getBoundingClientRect().height;

          return last == next;
        }, () => this.trigger(settings));
      });
    }
  }

  ngOnDestroy() {

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private trigger(settings: FocusDirectiveSettings) {
    if (this.focus.signature == settings.signature) {

      this.applyingSettings = {
        signature: settings.signature,
        behavior: settings.behavior ? settings.behavior : this.focus.behavior ? this.focus.behavior : 'instant',
        position: settings.position ? settings.position : this.focus.position ? this.focus.position : 'top',
        offset: settings.offset ? settings.offset : this.focus.offset ? this.focus.offset : 0
      };

      this.applyingSettings.offsetTop = settings.offsetTop ? settings.offsetTop : this.focus.offsetTop ? this.focus.offsetTop : this.applyingSettings.offset;
      this.applyingSettings.offsetBottom = settings.offsetBottom ? settings.offsetBottom : this.focus.offsetBottom ? this.focus.offsetBottom : this.applyingSettings.offset;
      this.applyingSettings.offsetCenter = settings.offsetCenter ? settings.offsetCenter : this.focus.offsetCenter ? this.focus.offsetCenter : this.applyingSettings.offset;

      this.scroll(this.applyingSettings, NaN);
    }
  }


  private scroll(settings: FocusDirectiveSettings, lastY: number) {

    if (!this.subscription || this.applyingSettings != settings) return;

    let scrollable = (this.ref.nativeElement as HTMLElement).scrollableElement();

    if (scrollable) {

      let rect: DOMRect = (this.ref.nativeElement as HTMLElement).getBoundingClientRect() as DOMRect;

      if (rect.y == lastY || rect.y >= settings.offsetTop && rect.y + rect.height <= window.innerHeight - settings.offsetBottom) return;

      let target: number;

      switch (settings.position) {
        case 'top':
          target = settings.offsetTop;
          break;

        case 'bottom':
          target = window.innerHeight - rect.height - settings.offsetBottom;
          break;

        case 'center':
          target = (window.innerHeight - rect.height) / 2 + settings.offsetCenter;
          break;

        case 'nearest':

          if (rect.height + settings.offsetTop + settings.offsetBottom >= window.innerHeight) {
            target = settings.offsetTop;
          }
          else {
            let candidates = [settings.offsetTop, window.innerHeight - rect.height - settings.offsetBottom, (window.innerHeight - rect.height) / 2 + settings.offsetCenter];
            let abs = candidates.map(i => Math.abs(rect.y - i));
            target = candidates[abs.indexOf(abs.min(i => i))];
          }
          break;

        default: return;
      }      

      if (Math.abs(rect.y - target) > 1) {

        switch (settings.behavior) {
          case 'instant':
            scrollable.scrollTop += Math.round(rect.y - target);
            break;

          case 'smooth':
            scrollable.scrollTop += Math.round(rect.y > target ? Math.max((rect.y - target) / 2, 1) : Math.min((rect.y - target) / 2, -1));
            break;

          default: return;
        }

        setTimeout(() => this.scroll(settings, rect.y), 16);
      }
      else {
        scrollable.scrollTop += rect.y - target;
      }
    }
  }
}

const html = document.body.parentElement;

function until(condition: () => boolean, then: () => void) {
  if (!condition()) {
    setTimeout(() => until(condition, then), 40);
  }
  else {
    then();
  }
}
